import React from 'react';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import { PieChart as PieChartIcon, TrendingUp } from 'lucide-react';

/**
 * Componente de 2 gráficas para Billing (Cloud Unificado)
 * - PIE: Precio por Fabricante
 * - LINEAL: Evolución de Costo Real y Precio Neto por mes
 */
export default function GraficasPieBilling({
  precioXFabricanteData = [],
  evolucionData = [],
  isDarkMode = true,
  onFabricanteClick = null,
  selectedFabricantes = []
}) {
  // Estilos
  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  // Paleta verde
  const FINOPS_COLORS = [
    '#00FF66',
    '#1EEB8A',
    '#34D399',
    '#2DD4BF',
    '#22C55E',
    '#10B981',
    '#4ADE80',
    '#6EE7B7',
    '#16A34A',
    '#059669'
  ];

  // Preparar datos para gráfico PIE
  const dataFabricantes = precioXFabricanteData.slice(0, 10).map(item => ({
    name: item.fabricante || 'Sin fabricante',
    value: parseFloat(item.precio_neto) || 0
  }));

  // Preparar datos para gráfico LINEAL (Evolución)
  // Mostrar los 12 meses del año completo
  const allMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Crear un mapa de datos por mes desde evolucionData
  const datosEvolucionMap = {};
  evolucionData.forEach(item => {
    const mesFormato = item.mes ? item.mes.split('-')[1] : '';
    const monthNames = {
      '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
      '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
    };
    const monthLabel = monthNames[mesFormato] || 'N/A';
    
    if (monthLabel !== 'N/A') {
      datosEvolucionMap[monthLabel] = {
        mes: monthLabel,
        precio_neto: parseFloat(item.precio_neto) || 0,
        costo_real: parseFloat(item.costo_real) || 0
      };
    }
  });
  
  // Construir array completo de 12 meses, llenando con datos si existen, 0 si no
  const dataEvolucion = allMonths.map(mes => 
    datosEvolucionMap[mes] || {
      mes: mes,
      precio_neto: 0,
      costo_real: 0
    }
  );

  // Custom Tooltip para PIE
  const CustomTooltipPie = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = dataFabricantes.reduce((sum, item) => sum + item.value, 0);
      const percent = total > 0 ? (data.value / total) * 100 : 0;
      
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40' 
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-semibold text-sm`}>{data.name}</p>
          <p className={`${textSecondary} text-sm`}>
            ${data.value.toLocaleString('es-MX', { maximumFractionDigits: 2 })}
          </p>
          <p className={`${textMuted} text-xs`}>
            {percent.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip para LINEAL
  const CustomTooltipLine = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40' 
            : 'bg-white/95 border-2 border-emerald-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-semibold text-sm`}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: ${entry.value.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Leyenda para PIE
  const CustomLegend = ({ data, colors, onItemClick = null, selectedValues = [] }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    const handleLegendItemClick = (itemName) => {
      if (onItemClick && typeof onItemClick === 'function') {
        onItemClick(itemName);
      }
    };

    return (
      <div 
        className="overflow-x-auto overflow-y-hidden pb-2 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full"
        style={{ 
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#00FF66 #052012' : '#10B981 #ECFDF5'
        }}
      >
        <div className="flex gap-4 min-w-max px-2">
          {data.map((entry, index) => {
            const percent = total > 0 ? (entry.value / total) * 100 : 0;
            const isSelected = selectedValues.includes(entry.name);

            return (
              <button
                key={`legend-${index}`}
                onClick={() => handleLegendItemClick(entry.name)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap transition-all ${
                  isSelected
                    ? isDarkMode
                      ? 'bg-[#00FF66]/20 border border-[#00FF66]/50'
                      : 'bg-emerald-100 border border-emerald-400'
                    : isDarkMode
                      ? 'bg-[#00FF66]/5 border border-[#00FF66]/20 hover:border-[#00FF66]/40'
                      : 'bg-emerald-50 border border-emerald-200 hover:border-emerald-400'
                }`}
              >
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
                <span className={`text-xs ${textSecondary}`}>
                  {entry.name} - ${entry.value.toLocaleString('es-MX', { maximumFractionDigits: 0 })} ({percent.toFixed(1)}%)
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      {/* Gráfico PIE: Precio por Fabricante */}
      {dataFabricantes.length > 0 ? (
        <div className={cardClasses}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <PieChartIcon className="w-5 h-5" style={{ color: '#00FF66' }} />
              <h3 className={`${textPrimary} text-lg font-bold`}>
                Precio por Fabricante
              </h3>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <PieChart data={dataFabricantes}>
                <Pie
                  data={dataFabricantes}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(state) => {
                    if (state && state.entry) {
                      onFabricanteClick?.(state.entry.name);
                    }
                  }}
                >
                  {dataFabricantes.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={FINOPS_COLORS[index % FINOPS_COLORS.length]}
                      style={{ cursor: onFabricanteClick ? 'pointer' : 'default' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltipPie />} />
              </PieChart>
            </ResponsiveContainer>

            <CustomLegend 
              data={dataFabricantes} 
              colors={FINOPS_COLORS}
              onItemClick={onFabricanteClick}
              selectedValues={selectedFabricantes}
            />
          </div>
        </div>
      ) : (
        <div className={cardClasses + ' p-6 flex items-center justify-center min-h-[400px]'}>
          <p className={textMuted}>Sin datos de fabricantes</p>
        </div>
      )}

      {/* Gráfico LINEAL: Evolución de Precio Neto y Costo Real */}
      {dataEvolucion.length > 0 ? (
        <div className={cardClasses}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-5 h-5" style={{ color: '#00FF66' }} />
              <h3 className={`${textPrimary} text-lg font-bold`}>
                Evolución Mensual
              </h3>
            </div>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dataEvolucion} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke={isDarkMode ? '#00FF66/20' : '#10B981/20'}
                />
                <XAxis 
                  dataKey="mes" 
                  stroke={isDarkMode ? '#a0c8b0' : '#666'}
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke={isDarkMode ? '#a0c8b0' : '#666'}
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltipLine />} />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  contentStyle={{
                    backgroundColor: isDarkMode ? '#03120a/80' : 'white/80',
                    border: `1px solid ${isDarkMode ? '#00FF66/40' : '#10B981/40'}`,
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="precio_neto" 
                  stroke="#00FF66" 
                  strokeWidth={2}
                  dot={{ fill: '#00FF66', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Precio Neto"
                />
                <Line 
                  type="monotone" 
                  dataKey="costo_real" 
                  stroke="#1EEB8A" 
                  strokeWidth={2}
                  dot={{ fill: '#1EEB8A', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Costo Real"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className={cardClasses + ' p-6 flex items-center justify-center min-h-[400px]'}>
          <p className={textMuted}>Sin datos de evolución</p>
        </div>
      )}
    </div>
  );
}